import { Tooltip } from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import {
  getCustomerSatisfactionStatusColor,
  getCustomerSatisfactionStatusLabel,
} from "../../../models/customer-satisfaction/enum";
import { IClientOrderSatisfaction } from "../../../models/customer-satisfaction/response";
import { IClientOrderSatisfactionQuery } from "../../../models/customer-satisfaction/query";
import {
  mapClientOrderSatisfaction,
  normalizeListingResponse,
} from "../../../models/customer-satisfaction/mapper";
import {
  getOrderStatus,
  getOrderStatusColor,
} from "../../../models/order/enum";
import EndPoints from "../../../services/end-points";
import IBaseListingResponse from "../../../utils/api/base-listing-response";
import { DEFAULT_QUERY } from "../../../utils/helpers/constants";
import Tag from "../../general/antd/tag";
import Table from "../../general/table-components/table";

const formatUserName = (user?: any) => {
  if (!user) return "-";
  return (
    user.name ||
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    `#${user.id}`
  );
};

const ClientOrderSatisfactionsTable = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { direction } = useContext(AppContext);
  const [query, setQuery] =
    useState<IClientOrderSatisfactionQuery>(DEFAULT_QUERY);
  const [list, setList] =
    useState<IBaseListingResponse<IClientOrderSatisfaction>>();
  const [loading, setLoading] = useState(false);

  const getData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const { data } = await EndPoints.client.getClientOrderSatisfactions(
        Number(id),
        query,
      );
      setList(normalizeListingResponse(data, mapClientOrderSatisfaction));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [id, query, direction]);

  const columns: ColumnProps<IClientOrderSatisfaction>[] = [
    {
      title: t("order_number"),
      dataIndex: "number",
      key: "number",
      align: "center",
      width: 130,
      render: (number, record) => (
        <Link to={`/orders/details/${record.id}`}>{number}</Link>
      ),
    },
    {
      title: t("current_order_status"),
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 170,
      render: (status) => (
        <Tag
          title={getOrderStatus(status)}
          color={getOrderStatusColor(status)}
        />
      ),
    },
    {
      title: t("duration"),
      dataIndex: "durationMinutes",
      key: "durationMinutes",
      align: "center",
      width: 110,
      render: (durationMinutes) =>
        durationMinutes === null || durationMinutes === undefined
          ? "-"
          : `${durationMinutes} ${t("minutes")}`,
    },
    {
      title: t("customer_satisfaction"),
      dataIndex: ["customerSatisfaction", "status"],
      key: "customerSatisfaction",
      align: "center",
      width: 130,
      render: (_, record) => (
        <Tag
          title={getCustomerSatisfactionStatusLabel(
            record.customerSatisfaction?.status,
          )}
          color={getCustomerSatisfactionStatusColor(
            record.customerSatisfaction?.status,
          )}
        />
      ),
    },
    {
      title: t("reason"),
      dataIndex: ["customerSatisfaction", "reason", "reason"],
      key: "reason",
      align: "center",
      ellipsis: true,
      render: (_, record) => record.customerSatisfaction?.reason?.reason ?? "-",
    },
    {
      title: t("note"),
      dataIndex: ["customerSatisfaction", "note"],
      key: "note",
      ellipsis: true,
      render: (_, record) => {
        const note = record.customerSatisfaction?.note;
        return note ? (
          <Tooltip title={note}>
            <span>{note}</span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("handling_method"),
      dataIndex: ["customerSatisfaction", "handlingMethod"],
      key: "handlingMethod",
      ellipsis: true,
      render: (_, record) => {
        const handlingMethod = record.customerSatisfaction?.handlingMethod;
        return handlingMethod ? (
          <Tooltip title={handlingMethod}>
            <span>{handlingMethod}</span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("customer_reaction"),
      dataIndex: ["customerSatisfaction", "customerReaction"],
      key: "customerReaction",
      ellipsis: true,
      render: (_, record) => {
        const customerReaction = record.customerSatisfaction?.customerReaction;
        return customerReaction ? (
          <Tooltip title={customerReaction}>
            <span>{customerReaction}</span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("determined_by"),
      dataIndex: ["customerSatisfaction", "determinedBy"],
      key: "determinedBy",
      align: "center",
      render: (_, record) =>
        formatUserName(record.customerSatisfaction?.determinedBy),
    },
    {
      title: t("determined_at"),
      dataIndex: ["customerSatisfaction", "determinedAt"],
      key: "determinedAt",
      align: "center",
      width: 170,
      render: (_, record) =>
        record.customerSatisfaction?.determinedAt
          ? moment(record.customerSatisfaction.determinedAt).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "-",
    },
  ];

  return (
    <Table<IClientOrderSatisfaction>
      rowKey="id"
      showPagination
      columns={columns}
      dataSource={list?.data ?? []}
      loading={loading || list === undefined}
      total={list?.total}
      pageSize={query.perPage}
      page={query.page}
      scroll={{ x: 1200 }}
      onPaginationChange={(page, perPage) => {
        setQuery((current) => ({
          ...current,
          page,
          perPage,
        }));
      }}
    />
  );
};

export default ClientOrderSatisfactionsTable;
